// src/components/ServiceCard.tsx
import React from "react";
import { Card, CardContent, Typography, Box, Stack, Chip, Avatar, useTheme } from "@mui/material";
import { CompanyService } from "../types/services";
import * as Icons from "@mui/icons-material";

interface Props {
  service: CompanyService;
}

export const ServiceCard: React.FC<Props> = ({ service }) => {
  const theme = useTheme();

  // Dynamically select the icon from MUI Icons library
  const IconComponent = (Icons as any)[service.icon] || Icons.SettingsSuggest;

  return (
    <Card
      elevation={2}
      sx={{
        width: "100%",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        transition: "transform 0.3s, box-shadow 0.3s",
        "&:hover": {
          transform: "translateY(-8px)",
          boxShadow: theme.shadows[10],
          borderColor: theme.palette.secondary.main,
        },
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 4,
        overflow: "hidden",
      }}
    >
      <CardContent sx={{ flexGrow: 1, p: 4 }}>
        {/* Icon Header */}
        <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
          <Avatar
            sx={{
              bgcolor: "secondary.light",
              width: 56,
              height: 56,
              mr: 2,
            }}
          >
            <IconComponent sx={{ fontSize: 32, color: "white" }} />
          </Avatar>
          <Typography variant="overline" color="text.secondary" fontWeight="bold">
            {service.category}
          </Typography>
        </Box>

        {/* Content */}
        <Typography variant="h5" component="h3" gutterBottom fontWeight="800">
          {service.title}
        </Typography>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 3, lineHeight: 1.7 }}>
          {service.description}
        </Typography>

        {/* Technical Chips */}
        <Box sx={{ mt: "auto", pt: 2 }}>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {service.technologies.map((tech) => (
              <Chip
                key={tech}
                label={tech}
                size="small"
                variant="outlined"
                sx={{
                  fontSize: "0.7rem",
                  fontWeight: 600,
                  borderRadius: "4px",
                }}
              />
            ))}
          </Stack>
        </Box>
      </CardContent>
    </Card>
  );
};
